import type { trackingAr } from './tracking.ar';

/** Visit gaps, shift activity and tracking copy (English). Must cover exactly the same keys as `trackingAr`. */
export const trackingEn: typeof trackingAr = {
  travel: {
    title: 'Time and distance between visits',
    description:
      'Per agent per day: the minutes between leaving one branch and checking in at the next, and the straight-line distance between the two branches. The straight line is not the road distance travelled.',
    straightLine: 'Straight-line distance',
    straightLineNote: 'Straight line between the two branches (not the road distance travelled).',
    gap: 'Gap',
    gapMinutes: 'Gap (min)',
    totalGap: 'Total gap',
    avgGap: 'Average gap',
    avgDistance: 'Average straight-line distance',
    avgVisits: 'Visits per day',
    pairs: 'Consecutive visits',
    // The CSV keeps the previous app's column header.
    pairsCsv: 'Pairs',
    unknownDistance: 'Distance unknown',
    unknownGap: 'Gap unknown',
    firstVisit: 'First visit of the day',
    byAgentDay: 'Per agent per day',
    byAgent: 'Per agent over the range',
    days: 'Days',
    visits: 'Visits',
    detailTitle: 'Visits of {{name}} on {{date}}',
    recompute: 'Recompute',
    recomputed: '{{count}} agent-days recomputed',
    recomputed_one: '{{count}} agent-day recomputed',
    recomputed_two: '{{count}} agent-days recomputed',
    recomputed_few: '{{count}} agent-days recomputed',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    flags: {
      NO_END_TIME: 'Previous visit has no end time',
      OVERLAP: 'Overlaps the previous visit by {{minutes}} min',
      INVERTED_TIMES: 'End time before start time (phone clock)',
      NO_COORDINATES: 'A branch has no verified location: distance unknown',
    },
  },
  activity: {
    title: 'Shifts, distance covered and coverage',
    description:
      'Per agent per day, from the shift tracking points: shift hours, visits, kilometres covered and the share of the shift actually covered by location points. A day with low coverage is not comparable with a full one.',
    shiftHours: 'Shift',
    walked: 'Distance covered',
    // The CSV and Excel keep the previous app's column header.
    walkedExport: 'Walked',
    coverage: 'Coverage',
    lowCoverage: 'Low coverage: not comparable with a full day',
    noTracking: 'No shift tracking',
    trackedDays: 'Days with tracking',
    avgCoverage: 'Average coverage',
    rangeTotal: 'Distance covered in the range',
    route: 'Route',
    kmUnit: 'km',
    hoursUnit: 'h',
  },
  tracking: {
    liveTitle: 'Live map',
    liveDescription:
      "Agents with an open shift. Each marker shows how long ago its last point was taken and that point's accuracy circle. Refreshed every {{seconds}} s. Every view is recorded in the audit log.",
    routeTitle: 'Daily route',
    routeDescription: 'One agent, one day: the path, time at each branch, idle periods and gaps in tracking with their likely cause.',
    states: {
      LIVE: 'Live',
      STALE: 'Stale',
      SHIFT_ENDED: 'Shift ended',
      CONSENT_MISSING: 'No tracking consent',
      PERMISSION_DENIED: 'Location permission denied',
      NO_POINT_YET: 'No point yet',
    },
    since: '{{time}} ago',
    accuracy: 'Accuracy',
    battery: 'Battery',
    mocked: 'Mock location',
    legend: 'Map legend',
    accuracyCircle: 'GPS accuracy',
    lastPoint: 'Last point',
    shiftStarted: 'Shift started',
    noAgents: 'No field agents.',
    timeline: 'Timeline',
    timelineKinds: { SHIFT_START: 'Shift started', SHIFT_END: 'Shift ended', VISIT: 'Visit', IDLE: 'Idle', GAP: 'Gap in tracking' },
    /** Not in the previous app (its timeline showed the server's Arabic text): the shift's end reason code, in words. */
    endReasons: {
      AGENT: 'Ended by the agent',
      AUTO_STOP: 'Stopped automatically at the set time',
      SERVER_AUTO_CLOSE: 'Closed by the server',
      CONSENT_WITHDRAWN: 'Tracking consent withdrawn',
    },
    gapReasons: {
      APP_CLOSED: 'App closed or stopped by the phone',
      PERMISSION_REVOKED: 'Location permission revoked',
      LOCATION_OFF: 'Location services off',
      BATTERY_SAVER: 'Battery saver on',
      NO_SIGNAL: 'No location signal',
    },
    pointsPurged: 'Detailed points for this day were deleted after the retention window. The daily summary is kept.',
    points: 'Points',
    idle: 'Idle periods',
    gaps: 'Gaps in tracking',
    timeAtBranches: 'Time at branches',
    chooseAgent: 'Choose an agent and a day.',
    settings: 'Tracking settings',
    settingsHint: 'Applied by the phones at the next app start or shift. Only a super admin can change them.',
    retention: 'Raw points kept for (days)',
    retentionNote:
      'Location points are the only data in the system with a retention window: they are deleted after {{days}} days. Daily summaries are kept permanently.',
    oldestPoint: 'Oldest stored point: {{date}} ({{count}} points)',
    oldestPoint_one: 'Oldest stored point: {{date}} ({{count}} point)',
    oldestPoint_two: 'Oldest stored point: {{date}} ({{count}} points)',
    oldestPoint_few: 'Oldest stored point: {{date}} ({{count}} points)',
    sampleMin: 'Fix every (s)',
    sampleMax: 'Point at least every (s)',
    sampleDistance: 'Or after moving (m)',
    accuracyMode: 'Accuracy',
    accuracyModes: { BALANCED: 'Balanced (saves battery)', HIGH: 'High (GPS)' },
    uploadInterval: 'Upload every (s)',
    uploadBatch: 'Or after this many points',
    reminderTime: 'Reminder if the shift is still open at',
    autoStop: 'Shift closes itself at',
    maxAccuracy: 'Kilometres: ignore points less accurate than (m)',
    minMovement: 'Kilometres: ignore moves shorter than (m)',
    maxSpeed: 'Kilometres: ignore jumps faster than (km/h)',
    stale: 'Live map: stale after (s)',
    poll: 'Live map refresh (s)',
    gapThreshold: 'Gap in tracking after (min)',
  },
};
